import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Link} from '../../common/components';
import {HTTP404Page} from '../../common/components/http404';
import {AuthedUrlWrapper} from '../../common/components/AuthedUrlWrapper';
import {Metadata} from '../../common/metadata';
import {escapeQuotesOnServer, getArticleLDJSON, isValidUsername, safelySerializeJSON, tryDecodeURI} from '../../common/util';

import {BaseProfilePage} from './profileBase';
import {ProfilePageHeader} from '../components/ProfilePageHeader';
import {gifOGMeta} from '../components/gifOGMeta';
import {UpsellPill} from '../components/UpsellPill';

import {maybeReturnComponent, subscribe, transformProps} from '../../../replete';


function LinkTag(
    {imageUrl, url, name}: {imageUrl: string, url: string, name: string}) {
    return <Link to={url}>
        <AuthedUrlWrapper url={imageUrl} childrenFunc={(imageUrl) => {
            return <div
                className="LinkTag"
                style={{
                    'background-image': `${escapeQuotesOnServer(`url("${imageUrl}")`)}`,
                }}
            >
                <span className="term">{name}</span>
            </div>;
        }}/>
    </Link>;
}


export function BrandedPartnerCategory(
    {category, profile}: {category: api.PartnerCategory, profile: api.User}) {
    return <div className="BrandedPartnerCategory">
        <h2 className="non-mobile-only">{category.title}</h2>
        <div className="category-links-list">
            {category.links.map((link, i) => {
                const {name, url, image, packid} = link;
                let imageUrl = image;
                if (profile.partnerbanner) {
                    imageUrl = image || profile.partnerbanner[690];
                }
                imageUrl = imageUrl || '';
                const defaultUrl = packid ?
                    `/packs/${packid}` :
                    // eslint-disable-next-line max-len
                    `/official/${profile.username}/${category.slug}/${link.slug}`;
                return <LinkTag
                    key={i}
                    imageUrl={imageUrl}
                    url={url || defaultUrl}
                    name={name}
                />;
            })}
        </div>
    </div>;
}

@subscribe({
    auth: ['api.auth'],
})
@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    props.authedUsername = context.authService.getUsername();

    return props;
})
@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    props.username = tryDecodeURI(props.match.params.username);
    props.mediatype = props.match.params.mediatype;
    props.ownsProfile = context.authService.ownsProfile(props.username);

    return props;
})
@maybeReturnComponent((props: jsx.Props) => {
    if (!isValidUsername(props.username)) {
        return HTTP404Page;
    }
})
@subscribe({
    pageState: ['ui.BaseProfilePage.*', 'username'],
    isMobile: ['ui.isMobile'],
})
@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    props.section = props.pageState.section;

    if (props.ownsProfile && props.section !== BaseProfilePage.FAVORITES) {
        context.store.call(
            'api.gifs.searchByUsername.*',
            [props.username, props.section, props.ownsProfile],
            'delete',
        );
        context.store.call(
            'api.memes.searchByUsername.*',
            [props.username, props.section, props.ownsProfile],
            'delete',
        );
        context.store.call(
            'api.stickers.searchByUsername.*',
            [props.username, props.section, props.ownsProfile],
            'delete',
        );
    }

    return props;
})
@subscribe({
    profile: ['api.profiles.*', 'username', 'authedUsername'],
    gifs: ['api.gifs.searchByUsername.*', 'username', 'section', 'ownsProfile'],
    memes: ['api.memes.searchByUsername.*', 'username', 'section', 'ownsProfile'],
    stickers: ['api.stickers.searchByUsername.*', 'username', 'section', 'ownsProfile'],
})
export class BrandedPartnerPage extends BaseProfilePage {
    renderProfilePageView(user: api.User, ownsProfile: boolean,
        memeList: api.Post[], stickerList: api.Post[], gifList: api.Post[],
    ): Element {
        const gettextSub = this.context.gtService.gettextSub;
        return <div className="content">
            { user.partnercategories?.map?.((category, i) => {
                return <BrandedPartnerCategory
                    key={i}
                    profile={user}
                    category={category}
                />;
            }) }
            { ownsProfile === true &&
                <div className="switch-view-link">
                    <a onClick={() => {
                        this.setSection(BaseProfilePage.FAVORITES);
                    }}>
                        {gettextSub('View Favorites')}
                    </a>
                </div>
            }
            { memeList.length > 0 && this.renderMemeListSection(memeList)}
            { stickerList.length > 0 &&
                this.renderStickerListSection(stickerList)}
            { gifList.length > 0 && this.renderGifListSection(gifList) }
        </div>;
    }
    renderPage() {
        this.debug('renderPage');
        if (this.props.profile.pending) {
            return <div></div>;
        }
        this.debug('renderPage', 'authData', this.props.authData);
        const [gifList, memeList, stickerList] = this.prepareItemLists();

        let articleLDJSON = {};
        if (this.props.gifs.results.length) {
            articleLDJSON = getArticleLDJSON(this, this.props.gifs.results[0]);
        }

        const {isMobile, section, mediatype, ownsProfile} = this.props;
        this.profilePageView = section === BaseProfilePage.PROFILE &&
            !mediatype;
        this.favoritesPageView = section === BaseProfilePage.FAVORITES &&
            !mediatype;
        this.memePageView = mediatype === BaseProfilePage.MEMES;
        this.stickerPageView = mediatype === BaseProfilePage.STICKERS;

        return (
            <div>
                <Metadata page={this}>
                    {gifOGMeta(this.props.gifs.results, this.context)}
                    <script type="application/ld+json" dangerouslySetInnerHTML={{__html: safelySerializeJSON(articleLDJSON)}}/>
                </Metadata>

                <div className={'BrandedPartnerPage page'}>
                    <ProfilePageHeader
                        profile={this.props.profile.user}
                        fullProfile={this.props.profile}
                    />
                    <div className="container">
                        { this.profilePageView &&
                            this.renderProfilePageView(
                                this.props.profile.user, ownsProfile, memeList,
                                stickerList, gifList) }
                        { this.favoritesPageView &&
                           this.renderFavoritesPageView(ownsProfile, gifList) }
                        { this.memePageView &&
                            this.renderMemePageView(
                                isMobile, memeList, gifList) }
                        { this.stickerPageView &&
                            this.renderStickerPageView(
                                isMobile, stickerList, gifList) }
                        <UpsellPill origin='bpp'/>
                    </div>
                </div>
            </div>
        );
    }
}
